import { ApiService } from '@/apiConnection/ApiService';
import { Urls } from '@/apiConnection/urls';
import { CustomInputComponent } from '@/components/custom-input/custom-input.component';
import { CustomModalComponent } from '@/components/custom-modal/custom-modal.component';
import { CustomSelectComponent } from '@/components/custom-select/custom-select.component';
import { LoadingComponent } from '@/components/loading/loading.component';
import { PayMethodFormComponent } from '@/pages/pay-methods/pay-method-form/pay-method-form.component';
import { TariffsFormComponent } from '@/pages/settings/tariffs-form/tariffs-form.component';
import { Utils } from '@/utils/utils';
import { COUNTRIES } from '@/utils/countries';
import { NIF_TYPES } from '@/utils/nif-types';
import { Component, Input, Output, EventEmitter, ChangeDetectorRef, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';


@Component({
  selector: 'app-client-form',
  imports: [
    CustomModalComponent,
    ReactiveFormsModule,
    CustomInputComponent,
    LoadingComponent,
    CustomSelectComponent,
  ],
  templateUrl: './client-form.component.html',
  styleUrl: './client-form.component.scss',
})
export class ClientFormComponent {
  @Input() id: number | string = 0;
  @Output() closeFunction = new EventEmitter<any>();
  @Output() acceptFunction = new EventEmitter<any>();

  public loading = signal(false);
  public saving = signal(false);

  public saveForm: FormGroup = new FormGroup({});

  public showModalImage = false

  public showOptionalFields = false;
  public dropdowns: any = {
    tariffs: [],
    payMethods: [],
    countries: COUNTRIES,
    nifTypes: NIF_TYPES,
  };

  private codeRetryCount = 0;

  public tariffsFormComponent = TariffsFormComponent
  public payMethodFormComponent = PayMethodFormComponent

  constructor(
    private apiService: ApiService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.createForm();

    if (this.id != 0) {
      this.getById(this.id);
    } else {
      this.getIdByCompany();
    }
    this.getDropdowns();
  }

  acceptFormFunction() {
    this.saveEntity()
  }

  closeFormFunction() {
    this.closeFunction.emit();
  }

  createForm() {
    this.saveForm = new FormGroup({
      // Datos Identificativos
      id_by_company: new FormControl(null),
      name: new FormControl(null, [Validators.required]),
      code: new FormControl(null),
      nif: new FormControl(null, [Validators.required]),
      nif_type: new FormControl('nif', [Validators.required]),
      accounting_account: new FormControl(null),
      cod_agente: new FormControl(null),
      commercial_claim: new FormControl(null),

      // Dirección y Localización
      address: new FormControl(null, [Validators.required]),
      city: new FormControl(null, [Validators.required]),
      post_office_box: new FormControl(null),
      province: new FormControl(null, [Validators.required]),
      postal_code: new FormControl(null, [Validators.required, Validators.minLength(5), Validators.maxLength(5), Validators.pattern(/^\d{5}$/)]),
      country: new FormControl('ES', [Validators.required]), // Valor por defecto: España

      // Contacto
      email_1: new FormControl(null, [Validators.required, Validators.email]),
      email_2: new FormControl(null, [Validators.email]),
      phone_1: new FormControl(null, [Validators.required]),
      phone_2: new FormControl(null),
      contact_person: new FormControl(null),

      // Configuración y Flags (Booleanos)
      send_email: new FormControl(true),
      iva: new FormControl(true),
      equivalence_recharge: new FormControl(false),
      credit_sales: new FormControl(true),
      payment_days: new FormControl(null),
      day_pay_1: new FormControl(null, [Validators.min(1), Validators.max(31)]),
      day_pay_2: new FormControl(null, [Validators.min(1), Validators.max(31)]),
      mailbox_office: new FormControl(null),

      // Valores Numéricos
      tariff_id: new FormControl(null, [Validators.min(0)]),
      discount: new FormControl(null, [Validators.min(0), Validators.max(100)]),
      payment_method_id: new FormControl(null),

      // Notas
      note: new FormControl(null),
    });
  }

  toggleOptionalFields() {
    this.showOptionalFields = !this.showOptionalFields;
  }

  getById(id: number | string) {
    this.loading.set(true);
    this.apiService.getById(Urls.urls.clients, id).subscribe({
      next: (data: any) => {
        this.saveForm.patchValue(data.client);
        this.loading.set(false);

        this.showOptionalFields = true;

        this.cdr.detectChanges();
      },
      error: (error: any) => {
        console.error('ERROR obtener productos. ' + error);
      },
      complete: () => {
        this.loading.set(false);
      },
    });
  }

  getDropdowns() {
    const config = { companies: {}, payMethods: {}, tariffs: {} };
    this.apiService.getSelectors(Urls.urls.selectors, config).subscribe({
      next: (data: any) => {
        const company = data.companies[0];
        const invoice_tariffs = JSON.parse(company.invoice_tariffs || '[]').map((tariff: any) => ({
          id: Number(tariff.id),
          name: `Tarifa ${tariff.id} (${tariff.quotient})`,
          tax: false
        }));

        this.dropdowns.tariffs = [
          { id: company.ticket_tariff, name: `Base (${company.ticket_tariff})`, tax: true },
          ...invoice_tariffs
        ];

        this.dropdowns.clients = data.clients || [];
        this.dropdowns.payMethods = data.payMethods || [];
        console.log('Dropdowns cargados:', data);
        this.cdr.detectChanges();

        if (invoice_tariffs.length > 0) {
          this.saveForm.get('tariff')?.patchValue(invoice_tariffs[0].id, { emitEvent: true });
        }
      }
    });
  }

  getIdByCompany() {
    this.apiService.getPetition(Urls.urls.clients + '/last-id-by-company').subscribe((data: any) => {
      this.saveForm.patchValue({ id_by_company: data.last_id_by_company + 1 });
      this.saveForm.patchValue({ code: String(data.last_id_by_company + 1).padStart(3, '0') });
      this.cdr.detectChanges();
    });
  }

  saveEntity() {
    if (this.saveForm.valid) {
      this.saving.set(true);

      this.apiService.save(Urls.urls.clients, this.saveForm.value, this.id).subscribe({
        next: (response: any) => {
          Utils.notification.success("Cliente guardado")
          this.codeRetryCount = 0;
          this.acceptFunction.emit(response);
          this.closeFormFunction()
        },
        error: (error: any) => {
          if (Utils.isCodeAlreadyTakenError(error) && this.codeRetryCount < Utils.MAX_CODE_ALREADY_TAKEN_RETRIES) {
            this.codeRetryCount++;
            this.saveForm.get('code')?.setValue(Utils.incrementCode(this.saveForm.get('code')?.value));
            this.saveEntity();
            return;
          }

          this.codeRetryCount = 0;
          this.saving.set(false);
          Utils.notification.alert(Utils.extractErrorMessage(error) || "Error al guardar el cliente")
        },
        complete: () => {
          this.saving.set(false);
        }
      })
    } else {
      Utils.notification.alert("Debe completar todos los campos obligatorios")
    }
  }

}
